import { ChangeDetectionStrategy, Component, computed, input, ViewEncapsulation } from "@angular/core";
import { FieldTree } from "@angular/forms/signals";
import { Layouts } from "./se.layouts";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { S25SimpleCollapseModule } from "../../s25-simple-collapse/s25.simple.collapse.module";
import { S25Util } from "../../../util/s25-util";
import { SeLayoutConfigSettingComponent } from "./se.layout.config.setting.component";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { S25InfoMessageModule } from "../../s25-info-message/s25.info.message.module";

@Component({
    standalone: true,
    selector: "s25-ng-se-layout-config-widget",
    template: `
        <div class="form-row">
            <label for="layout-config--widget-type">Widget Type</label>
            @if (readonly()) {
                <p>{{ widgetMeta().name }}</p>
            } @else {
                <select
                    id="layout-config--widget-type"
                    [ngModel]="widget().type().value()"
                    (ngModelChange)="setWidgetType($event)"
                    name="widget-type"
                    class="cn-form__control"
                >
                    @for (option of widgetOptions(); track option) {
                        <option [value]="option.id">
                            {{ option.name }}
                        </option>
                    }
                </select>
            }
        </div>
        <div class="form-row visibility">
            <div class="visibility-label">
                <label for="layout-config--widget-visibility">Visible To Roles</label>
                <s25-ng-info-message [isPopover]="true">
                    Select roles that can see this widget. Selecting no roles will make the widget visible to all users.
                </s25-ng-info-message>
            </div>
            @if (readonly()) {
                @if (!!visibleToRoles().length) {
                    <s25-ng-multiselect-results [selectedItems]="visibleToRoles()" [disabled]="true" />
                } @else {
                    <p>All</p>
                }
            } @else {
                <s25-ng-multiselect-search-criteria
                    [type]="'organizationRoles'"
                    [selectedItems]="visibleToRoles()"
                    [showResults]="true"
                    (onDone)="setVisibleToRoles($event)"
                />
                @for (error of widget().visibleToRoles().errors(); track $index) {
                    <p class="field-error">{{ error.message }}</p>
                }
            }
        </div>
        @for (setting of widgetMeta().settings; track setting.id) {
            <s25-ng-se-layout-config-setting
                class="setting"
                [meta]="setting"
                [settings]="widget().settings"
                [readonly]="readonly()"
            />
        }
    `,
    styles: `
        .form-row {
            padding-bottom: 1rem;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        S25SimpleCollapseModule,
        FormsModule,
        SeLayoutConfigSettingComponent,
        S25MultiselectModule,
        S25InfoMessageModule,
    ],
})
export class SeLayoutConfigWidgetComponent {
    readonly pageMeta = input.required<Layouts.PageConfig>();
    readonly widget = input.required<FieldTree<Layouts.Widget>>();
    readonly readonly = input(false);

    readonly widgetMeta = computed(() => {
        const widgets = this.pageMeta().widgets;
        return widgets[this.widget().type().value()].META;
    });

    readonly widgetOptions = computed(() => {
        return S25Util.entries(this.pageMeta().widgets).map(([widget, component]) => ({
            id: widget,
            name: component.META.name,
        }));
    });
    readonly visibleToRoles = computed(() => {
        return this.widget()
            .visibleToRoles()
            .value()
            .map((role) => ({ itemId: role }));
    });

    setWidgetType(type: string) {
        const widget = this.widget();
        if (widget.type().value() === type) return;
        const newWidgetMeta = this.pageMeta().widgets[type].META;
        const defaultSettings = Layouts.getDefaultSettingsForWidget(newWidgetMeta);
        widget.type().value.set(type);
        widget.settings().value.set(defaultSettings);
        widget.type().markAsDirty();
        widget.settings().markAsDirty();
    }

    setVisibleToRoles(roles: S25ItemI[]) {
        this.widget()
            .visibleToRoles()
            .value.set(roles.map((role) => <number>role.itemId));
        this.widget().visibleToRoles().markAsDirty();
    }
}
